import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Megaphone, Plus, Search, X, Loader2, LogOut, UserRound, Clock, CheckCircle2, XCircle, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { SCHOOL_LOGO, SCHOOL_NAME, STAGES } from "../../lib/constants";
import {
  getGuardianToken, clearGuardianToken, guardianMe, myChildren,
  searchStudentByNationalId, linkStudent, createPickupRequest, cancelPickupRequest,
} from "../lib/pickupApi";

const STATUS_LABELS = {
  new: { text: "طلب جديد — بانتظار المدرسة", color: "text-amber-700 bg-amber-50 border-amber-200" },
  exited: { text: "خرج الطالب من الفصل، في الطريق", color: "text-blue-700 bg-blue-50 border-blue-200" },
  on_the_way: { text: "في طريقه إلى نقطة التسليم", color: "text-blue-700 bg-blue-50 border-blue-200" },
};

function Countdown({ until, onDone }) {
  const [left, setLeft] = useState(Math.max(0, Math.floor((new Date(until) - new Date()) / 1000)));
  useEffect(() => {
    const t = setInterval(() => {
      setLeft((prev) => {
        if (prev <= 1) { clearInterval(t); onDone && onDone(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);
  const m = Math.floor(left / 60).toString().padStart(2, "0");
  const s = (left % 60).toString().padStart(2, "0");
  return <span className="tabular-nums" dir="ltr">{m}:{s}</span>;
}

export default function PickupChildren() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLink, setShowLink] = useState(false);
  const [nid, setNid] = useState("");
  const [searching, setSearching] = useState(false);
  const [foundStudent, setFoundStudent] = useState(null);
  const [linking, setLinking] = useState(false);
  const [confirming, setConfirming] = useState(null); // student pending confirm-call
  const [requesting, setRequesting] = useState(false);
  const [unavailableMsg, setUnavailableMsg] = useState(null);
  const pollRef = useRef(null);

  const load = async (silent = false) => {
    try {
      const list = await myChildren();
      setChildren(list);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const interval = setInterval(() => { load(true); }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!getGuardianToken()) { nav("/pickup/login"); return; }
    (async () => {
      try {
        const [m] = await Promise.all([guardianMe()]);
        setMe(m);
        await load();
      } catch {
        nav("/pickup/login");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const onLogout = () => { clearGuardianToken(); nav("/pickup/login"); };

  const doSearch = async () => {
    if (!nid.trim()) return;
    setSearching(true);
    setFoundStudent(null);
    try {
      const s = await searchStudentByNationalId(nid.trim());
      setFoundStudent(s);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "لم يتم العثور على طالب بهذا الرقم.");
    } finally {
      setSearching(false);
    }
  };

  const doLink = async () => {
    if (!foundStudent) return;
    setLinking(true);
    try {
      await linkStudent(foundStudent.id);
      toast.success("تم ربط الطالب بنجاح");
      setShowLink(false); setNid(""); setFoundStudent(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الربط");
    } finally {
      setLinking(false);
    }
  };

  const doRequest = async (student) => {
    setRequesting(true);
    try {
      const req = await createPickupRequest(student.id);
      toast.success(`تم إرسال طلب استدعاء ${student.name} بنجاح — رقم الطلب: ${req.request_number}`);
      setConfirming(null);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (typeof detail === "string" && detail.includes("غير متاح حاليًا")) {
        setConfirming(null);
        setUnavailableMsg(detail);
      } else {
        toast.error(detail || "تعذر إرسال الطلب");
      }
    } finally {
      setRequesting(false);
    }
  };

  const doCancel = async (req) => {
    try {
      await cancelPickupRequest(req.id);
      toast.success("تم إلغاء الطلب");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الإلغاء");
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F6FAF9]"><Loader2 className="animate-spin text-sky-700" size={28} /></div>;
  }

  return (
    <div dir="rtl" className="min-h-screen bg-[#F6FAF9]">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-50 ring-1 ring-sky-200/60 flex items-center justify-center overflow-hidden">
              <img src={SCHOOL_LOGO} alt="" className="w-8 h-8 object-contain" />
            </div>
            <div>
              <div className="font-cairo font-extrabold text-sky-800 text-sm sm:text-base">{SCHOOL_NAME}</div>
              <div className="text-[11px] text-slate-500">نظام استدعاء الطلاب</div>
            </div>
          </div>
          <Button variant="outline" onClick={onLogout} className="h-9 gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50 text-xs">
            <LogOut size={14} /> خروج
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 mb-1">
          <Megaphone className="text-sky-700" size={22} />
          <h1 className="font-cairo text-2xl font-extrabold text-slate-900">أبنائي</h1>
        </div>
        <p className="text-sm text-slate-500 mb-6">مرحبًا {me?.full_name} — تابع أبناءك واستدعِهم بسهولة عند الحاجة.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {children.map((s) => {
            const active = s.active_request;
            const statusInfo = active ? STATUS_LABELS[active.status] : null;
            const locked = !active && s.can_request_after && new Date(s.can_request_after) > new Date();
            return (
              <div key={s.id} className={`rounded-2xl bg-white border p-5 shadow-sm ${active ? "border-amber-300 ring-1 ring-amber-200" : "border-slate-200"}`} data-testid={`child-card-${s.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-14 h-14 rounded-xl bg-sky-50 ring-1 ring-sky-200/60 flex items-center justify-center shrink-0">
                    <UserRound size={26} className="text-sky-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-cairo font-bold text-slate-900">{s.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{STAGES[s.stage] || s.stage} — {s.grade} {s.section && `— فصل ${s.section}`}</div>
                  </div>
                </div>

                {statusInfo && (
                  <div className={`mt-3 text-xs px-3 py-2 rounded-lg border ${statusInfo.color} flex items-center gap-1.5`}>
                    <Clock size={13} /> {statusInfo.text}
                    <span className="mr-auto tabular-nums opacity-70">#{active.request_number}</span>
                  </div>
                )}

                <div className="mt-4">
                  {active ? (
                    active.status === "new" ? (
                      <Button variant="outline" onClick={() => doCancel(active)} className="w-full h-10 gap-2 text-rose-700 border-rose-200 hover:bg-rose-50" data-testid={`cancel-${s.id}`}>
                        <XCircle size={15} /> إلغاء الطلب
                      </Button>
                    ) : (
                      <div className="text-center text-xs text-slate-400 py-2">الطلب قيد التنفيذ من المدرسة</div>
                    )
                  ) : locked ? (
                    <Button disabled className="w-full h-10 gap-2 bg-slate-100 text-slate-500">
                      <Clock size={15} /> يمكنك الاستدعاء بعد <Countdown until={s.can_request_after} onDone={load} />
                    </Button>
                  ) : (
                    <Button onClick={() => setConfirming(s)} className="w-full h-10 gap-2 bg-sky-700 hover:bg-sky-800 text-white" data-testid={`request-${s.id}`}>
                      <Megaphone size={15} /> استدعاء الطالب
                    </Button>
                  )}
                </div>
              </div>
            );
          })}

          <button onClick={() => setShowLink(true)} className="rounded-2xl border-2 border-dashed border-sky-300 text-sky-700 hover:bg-sky-50 flex flex-col items-center justify-center gap-2 p-8 transition-colors" data-testid="btn-link-new-student">
            <Plus size={26} />
            <span className="font-cairo font-bold text-sm">ربط طالب جديد</span>
          </button>
        </div>
      </main>

      {/* نافذة ربط طالب جديد */}
      {showLink && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowLink(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-cairo font-bold text-slate-900 flex items-center gap-2"><Link2 size={18} className="text-sky-700" /> ربط طالب جديد</h3>
              <button onClick={() => setShowLink(false)}><X size={18} className="text-slate-400" /></button>
            </div>
            <Label className="text-xs">رقم هوية الطالب</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={nid} onChange={(e) => setNid(e.target.value)} placeholder="رقم الهوية" className="h-10" dir="ltr" data-testid="link-national-id" />
              <Button onClick={doSearch} disabled={searching} className="h-10 gap-1.5 bg-sky-700 hover:bg-sky-800 text-white shrink-0">
                {searching ? <Loader2 className="animate-spin" size={15} /> : <Search size={15} />}
              </Button>
            </div>

            {foundStudent && (
              <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                <div className="font-cairo font-bold text-slate-900 text-sm">{foundStudent.name}</div>
                <div className="text-xs text-slate-600 mt-1">{STAGES[foundStudent.stage] || foundStudent.stage} — {foundStudent.grade} {foundStudent.section && `— فصل ${foundStudent.section}`}</div>
                <Button onClick={doLink} disabled={linking} className="w-full h-9 mt-3 gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm">
                  {linking ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />} تأكيد الربط
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* نافذة "النظام غير متاح حاليًا" - كبيرة وملفتة للنظر */}
      {unavailableMsg && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setUnavailableMsg(null)}>
          <div className="bg-white rounded-2xl p-8 w-full max-w-md text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-4">
              <Clock className="text-amber-600" size={32} />
            </div>
            <h2 className="font-cairo text-xl font-extrabold text-amber-700 mb-3">نظام استدعاء الطالب غير متاح حاليًا</h2>
            <p className="text-slate-700 text-base leading-relaxed font-medium">{unavailableMsg}</p>
            <Button onClick={() => setUnavailableMsg(null)} className="w-full h-11 mt-6 bg-amber-600 hover:bg-amber-700 text-white font-cairo font-bold">
              حسنًا
            </Button>
          </div>
        </div>
      )}

      {/* نافذة تأكيد الاستدعاء */}
      {confirming && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setConfirming(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm text-center" onClick={(e) => e.stopPropagation()}>
            <Megaphone className="mx-auto text-sky-700 mb-3" size={32} />
            <p className="font-cairo font-bold text-slate-900">هل أنت متأكد من رغبتك في استدعاء الطالب «{confirming.name}»؟</p>
            <div className="flex gap-2 mt-5">
              <Button variant="outline" onClick={() => setConfirming(null)} className="flex-1 h-10">إلغاء</Button>
              <Button onClick={() => doRequest(confirming)} disabled={requesting} className="flex-1 h-10 bg-sky-700 hover:bg-sky-800 text-white gap-1.5">
                {requesting ? <Loader2 className="animate-spin" size={14} /> : null} تأكيد الاستدعاء
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
